import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Box, Typography, CircularProgress } from '@mui/material';
import { apiCache } from '@/services';
import type { ItemBlueprint } from '@/types';

interface BlueprintSelectorProps {
  value: ItemBlueprint | null;
  onChange: (blueprint: ItemBlueprint | null) => void;
  disabled?: boolean;
}

export const BlueprintSelector: React.FC<BlueprintSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [blueprints, setBlueprints] = useState<ItemBlueprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadBlueprints = async () => {
      try {
        setLoading(true);
        const data = await apiCache.getItemBlueprints();
        // Sort by category then name to avoid duplicate group headers
        const sorted = [...data].sort((a, b) => {
          const categoryCompare = a.category_name.localeCompare(b.category_name);
          if (categoryCompare !== 0) return categoryCompare;
          return a.name.localeCompare(b.name);
        });
        setBlueprints(sorted);
        setError(null);
      } catch (err) {
        setError('Failed to load blueprints');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadBlueprints();
  }, []);

  return (
    <Box>
      <Autocomplete
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        options={blueprints}
        getOptionLabel={(option) => option.name}
        groupBy={(option) => option.category_name}
        loading={loading}
        disabled={disabled || loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Blueprint"
            placeholder="Search for a blueprint..."
            error={!!error}
            helperText={error || `${blueprints.length} blueprints available`}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          // Extract key from props to avoid React warning
          const { key: _key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & {
            key: string;
          };
          return (
            <Box component="li" key={option.item_id} {...optionProps}>
              <Box>
                <Typography variant="body1">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.group_name} • Level {option.skill_level}
                </Typography>
              </Box>
            </Box>
          );
        }}
        isOptionEqualToValue={(option, value) => option.item_id === value.item_id}
      />
    </Box>
  );
};
